// src/components/Locations/FamilyLinksTab.js
// Staff management of Tier 1 family photo links per location
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Chip, Tooltip, Alert, CircularProgress,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Block as BlockIcon,
  Link as LinkIcon,
  Photo as PhotoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { familyLinksApi, personsApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

const RELATIONSHIPS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'grandchild', label: 'Grandchild' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
];

const FamilyLinksTab = ({ locationId }) => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [links, setLinks] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    personId: '',
    familyName: '',
    familyEmail: '',
    relationship: '',
  });

  // Photos dialog state
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [linkPhotos, setLinkPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  const fetchLinks = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await familyLinksApi.getAll({ locationId });
      setLinks(response.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load family links');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  const fetchPersons = useCallback(async () => {
    if (!locationId) return;
    try {
      const response = await personsApi.getByLocation(locationId);
      setPersons(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to load persons:', err);
    }
  }, [locationId]);

  useEffect(() => {
    fetchLinks();
    fetchPersons();
  }, [fetchLinks, fetchPersons]);

  const handleCreate = async () => {
    if (!formData.personId) {
      enqueueSnackbar('Please select a resident', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await familyLinksApi.create({
        personId: parseInt(formData.personId),
        locationId,
        familyName: formData.familyName || null,
        familyEmail: formData.familyEmail || null,
        relationship: formData.relationship || null,
      });
      enqueueSnackbar('Family link created successfully', { variant: 'success' });
      setDialogOpen(false);
      setFormData({ personId: '', familyName: '', familyEmail: '', relationship: '' });
      fetchLinks();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to create link', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (linkId) => {
    try {
      await familyLinksApi.deactivate(linkId);
      enqueueSnackbar('Family link deactivated', { variant: 'success' });
      fetchLinks();
    } catch (err) {
      enqueueSnackbar('Failed to deactivate link', { variant: 'error' });
    }
  };

  const handleCopyLink = (linkUrl) => {
    navigator.clipboard.writeText(linkUrl).then(() => {
      enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    });
  };

  const handleViewPhotos = async (link) => {
    setSelectedLink(link);
    setPhotosDialogOpen(true);
    setPhotosLoading(true);
    try {
      const response = await familyLinksApi.getPhotos(link.id, { limit: 50 });
      setLinkPhotos(response.data?.data || []);
    } catch (err) {
      enqueueSnackbar('Failed to load photos', { variant: 'error' });
    } finally {
      setPhotosLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(new Date(dateString));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Family Photo Links
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchLinks}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Create Link
          </Button>
        </Stack>
      </Box>

      {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && links.length === 0 && !error && (
        <Alert severity="info">
          No family links for this location yet. Create one to allow family members to upload photos.
        </Alert>
      )}

      {links.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Resident</TableCell>
                <TableCell>Family Member</TableCell>
                <TableCell>Relationship</TableCell>
                <TableCell align="center">Photos</TableCell>
                <TableCell>Last Access</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link) => (
                <TableRow key={link.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {link.residentName || `Person #${link.personId}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {link.familyName || '-'}
                    </Typography>
                    {link.familyEmail && (
                      <Typography variant="caption" color="text.secondary">
                        {link.familyEmail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={link.relationship || 'unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={link.photosUploaded || 0}
                      size="small"
                      color={link.photosUploaded > 0 ? 'primary' : 'default'}
                      onClick={() => link.photosUploaded > 0 && handleViewPhotos(link)}
                      sx={{ cursor: link.photosUploaded > 0 ? 'pointer' : 'default' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(link.lastAccessedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(link.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Copy link URL">
                        <IconButton size="small" onClick={() => handleCopyLink(link.linkUrl)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {link.photosUploaded > 0 && (
                        <Tooltip title="View photos">
                          <IconButton size="small" onClick={() => handleViewPhotos(link)}>
                            <PhotoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Deactivate link">
                        <IconButton size="small" color="error" onClick={() => handleDeactivate(link.id)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Family Link Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Family Photo Link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Resident"
              value={formData.personId}
              onChange={(e) => setFormData(prev => ({ ...prev, personId: e.target.value }))}
              fullWidth
              required
            >
              {persons.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.friendlyName || `${p.firstName} ${p.lastName}` || `Person #${p.id}`}
                </MenuItem>
              ))}
              {persons.length === 0 && (
                <MenuItem disabled>No residents at this location</MenuItem>
              )}
            </TextField>
            <TextField
              label="Family Member Name"
              placeholder="e.g. Maria (Daughter)"
              value={formData.familyName}
              onChange={(e) => setFormData(prev => ({ ...prev, familyName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={formData.familyEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, familyEmail: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Relationship"
              value={formData.relationship}
              onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
              fullWidth
            >
              {RELATIONSHIPS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </TextField>
            <Alert severity="info" sx={{ mt: 1 }}>
              The family member will receive a unique link. On first visit, they&apos;ll set a 4-digit PIN to secure access.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !formData.personId}
            startIcon={saving ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            Create Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photos Preview Dialog */}
      <Dialog open={photosDialogOpen} onClose={() => setPhotosDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Photos from {selectedLink?.familyName || 'Family Member'}
          <Typography variant="caption" display="block" color="text.secondary">
            For: {selectedLink?.residentName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {photosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : linkPhotos.length === 0 ? (
            <Alert severity="info">No photos uploaded yet.</Alert>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1.5,
              mt: 1
            }}>
              {linkPhotos.map((photo) => (
                <Box key={photo.id} sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={photo.thumbnailUrl || photo.url}
                    alt={photo.description || 'Photo'}
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      objectFit: 'cover',
                      borderRadius: 1,
                      bgcolor: 'grey.200'
                    }}
                  />
                  {photo.reaction && photo.reaction !== 'neutral' && (
                    <Chip
                      label={photo.reaction === 'liked' ? 'Liked' : 'Hidden'}
                      size="small"
                      color={photo.reaction === 'liked' ? 'success' : 'default'}
                      sx={{ position: 'absolute', top: 4, right: 4, fontSize: '0.65rem' }}
                    />
                  )}
                  <Typography variant="caption" display="block" noWrap sx={{ mt: 0.5 }}>
                    {photo.description || formatDate(photo.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotosDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FamilyLinksTab;
